import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface VideoPlayerProps {
  videoSrc: string;
  title: string;
  description: string;
  lightBackground?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoSrc,
  title,
  description,
  lightBackground = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setIsPlaying(true);
    setTimeout(() => {
      videoRef.current?.play();
    }, 100);
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
  };

  return (
    <div className="video-player-container">
      <motion.div
        className={`video-player-wrapper ${lightBackground ? 'video-player-light-bg' : ''}`}
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="video-player-content">
          {/* Video Element (always rendered) */}
          <video
            ref={videoRef}
            className="video-element"
            onEnded={handleVideoEnd}
            controls={isPlaying}
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Overlay with Title, Description, and Play Button */}
          <AnimatePresence>
            {!isPlaying && (
              <motion.div
                className="video-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="overlay-content">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h3 className="overlay-title">{title}</h3>
                    <p className="overlay-description">{description}</p>
                  </motion.div>

                  {/* Large Play Button */}
                  <motion.button
                    className="overlay-play-button"
                    onClick={handlePlay}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Play video"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                  >
                    <motion.div
                      className="play-pulse"
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.5, 0, 0.5]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 80 80"
                      fill="none"
                      className="play-svg"
                    >
                      <circle cx="40" cy="40" r="40" fill="white" fillOpacity="0.95" />
                      <path
                        d="M32 24L32 56L58 40L32 24Z"
                        fill="#2196F3"
                      />
                    </svg>
                  </motion.button>

                  <motion.div
                    className="play-hint"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    <i className="fas fa-hand-pointer"></i>
                    <span>Click to Watch</span>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <style>{`
        .video-player-container {
          margin: 3rem 0;
          display: flex;
          justify-content: center;
        }

        .video-player-wrapper {
          width: 100%;
          max-width: 900px;
          padding: 0;
          transition: all 0.4s ease;
        }

        .video-player-wrapper:hover {
          transform: translateY(-5px);
        }

        .video-player-content {
          position: relative;
          width: 100%;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
          aspect-ratio: 16 / 9;
        }

        .video-element {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        /* Overlay with Gradient Background */
        .video-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg,
            rgba(33, 150, 243, 0.95) 0%,
            rgba(156, 39, 176, 0.9) 50%,
            rgba(233, 30, 99, 0.95) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
        }

        .video-player-light-bg .video-overlay {
          background: linear-gradient(135deg,
            rgba(255, 255, 255, 0.97) 0%,
            rgba(227, 242, 253, 0.95) 100%);
        }

        .overlay-content {
          text-align: center;
          padding: 2rem;
          max-width: 90%;
        }

        .overlay-title {
          color: white;
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
          text-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
          line-height: 1.2;
        }

        .video-player-light-bg .overlay-title {
          color: #1e3c72;
          text-shadow: none;
        }

        .overlay-description {
          color: rgba(255, 255, 255, 0.95);
          font-size: 1.1rem;
          margin-bottom: 2.5rem;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          line-height: 1.6;
        }

        .video-player-light-bg .overlay-description {
          color: #555;
          text-shadow: none;
        }

        /* Large Play Button */
        .overlay-play-button {
          position: relative;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          outline: none;
          margin: 0 auto 1.5rem;
          display: block;
        }

        .play-pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 100px;
          background: white;
          border-radius: 50%;
          pointer-events: none;
        }

        .play-svg {
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.4));
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
        }

        .overlay-play-button:hover .play-svg circle {
          fill: #2196F3;
        }

        .overlay-play-button:hover .play-svg path {
          fill: white;
        }

        .play-hint {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .video-player-light-bg .play-hint {
          color: #2196F3;
          text-shadow: none;
        }

        .play-hint i {
          font-size: 1.2rem;
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        /* Responsive Design */
        @media screen and (max-width: 768px) {
          .overlay-title {
            font-size: 1.8rem;
          }

          .overlay-description {
            font-size: 1rem;
            margin-bottom: 2rem;
          }

          .overlay-content {
            padding: 1.5rem;
          }

          .play-svg {
            width: 70px;
            height: 70px;
          }

          .play-pulse {
            width: 80px;
            height: 80px;
          }

          .play-hint {
            font-size: 0.9rem;
          }
        }

        @media screen and (max-width: 480px) {
          .overlay-title {
            font-size: 1.4rem;
            margin-bottom: 0.75rem;
          }

          .overlay-description {
            font-size: 0.9rem;
            margin-bottom: 1.5rem;
          }

          .overlay-content {
            padding: 1rem;
          }

          .play-svg {
            width: 60px;
            height: 60px;
          }

          .play-pulse {
            width: 70px;
            height: 70px;
          }

          .play-hint {
            font-size: 0.85rem;
          }
        }

        /* Print Styles */
        @media print {
          .video-player-container,
          .video-player-wrapper {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default VideoPlayer;
